/**
 * DecisionFlow — CoSellManager
 * data-fit LLC · Confidential
 * Domain: P3-06 Channel / Co-Sell Compensation
 * Tier: Enterprise — feature_gate_key = 'feature.tier.cosell'
 * Spec authority: decisionflow_phase3_plan_spec_v1_0.md §7
 * API Contract: api_contract_v1_32.md §52
 * Wizard substages:
 *   partner_entity_setup · cosell_agreement_entry · cosell_agreement_approval
 *
 * Three tabs:
 *   Tab 1 — Partners: create / edit / deactivate partner_entity records
 *   Tab 2 — Agreements: create / edit / submit / approve cosell_agreement per credit
 *   Tab 3 — Results: view cosell_payout_result rows for a calculation run
 *
 * DDL ground truth (phase3_plan_spec §7.2):
 *   partner_entity:
 *     partner_entity_id uuid PK, tenant_id uuid,
 *     partner_code varchar(60) UNIQUE within tenant,
 *     partner_name varchar(255), partner_type varchar(30),
 *     CHECK(partner_type IN ('reseller','distributor','technology_partner','referral')),
 *     contact_email varchar(255) nullable, is_active bool,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *
 *   cosell_agreement:
 *     cosell_agreement_id uuid PK, tenant_id uuid,
 *     partner_entity_id uuid FK, position_id uuid FK, credit_id uuid FK,
 *     partner_commission_pct numeric(5,4) nullable,
 *     partner_commission_amount numeric(18,4) nullable,
 *     CHECK: exactly one of pct/amount must be non-null (XOR constraint)
 *     agreement_date date NN — must be <= deal_close_date,
 *     notes text nullable,
 *     submitted_by uuid, submitted_at timestamptz,
 *     approved_by uuid, approved_at timestamptz,
 *     created_by uuid, created_at timestamptz, updated_at timestamptz
 *
 *   cosell_payout_result:
 *     cosell_result_id uuid PK, calculation_run_id uuid,
 *     cosell_agreement_id uuid, partner_entity_id uuid, position_id uuid,
 *     fiscal_period_id int, credit_id uuid, deal_value numeric(18,4),
 *     partner_commission_pct numeric(5,4) nullable, partner_commission_amount numeric(18,4),
 *     payout_currency char(3), created_at timestamptz
 *
 * API surface (api_contract_v1_32.md §52):
 *   POST   /api/v1/partner-entities
 *   GET    /api/v1/partner-entities?partner_type={t}&is_active={bool}
 *   GET    /api/v1/partner-entities/{partner_entity_id}
 *   PUT    /api/v1/partner-entities/{partner_entity_id}              — raw fetch
 *   DELETE /api/v1/partner-entities/{partner_entity_id}             — raw fetch (soft-delete: sets is_active=FALSE)
 *   POST   /api/v1/cosell-agreements
 *   GET    /api/v1/cosell-agreements?credit_id={id}&partner_entity_id={id}&position_id={id}
 *   GET    /api/v1/cosell-agreements/{cosell_agreement_id}
 *   PUT    /api/v1/cosell-agreements/{cosell_agreement_id}          — raw fetch
 *   POST   /api/v1/cosell-agreements/{cosell_agreement_id}/submit
 *   POST   /api/v1/cosell-agreements/{cosell_agreement_id}/approve
 *   GET    /api/v1/cosell-payout-results?calculation_run_id={id}&partner_entity_id={id}&position_id={id}
 *
 * Architecture rules:
 *   - useAuth() for { user } — no token in props (ARCH-2)
 *   - apiGet / apiPost from client — PUT / DELETE via raw fetch
 *   - tenant_id never in any request body
 *   - No window.prompt / window.confirm — inline deactivation confirmation state
 *   - ApprovalPanel for cosell_agreement (submitted_by real column, onReject={null})
 *   - 403 FEATURE_UNAVAILABLE → GateStatusBanner
 *   - DELETE is soft: sets is_active=FALSE — blocked if PARTNER_ENTITY_HAS_ACTIVE_AGREEMENTS
 *
 * Props:
 *   mode             — 'wizard' | 'admin'
 *   period           — { fiscal_period_id, period_code } | null
 *   wizardSteps      — array (wizard context)
 *   embedded         — bool  — reserved; these managers are tenant-scoped
 *                              (COMPENSATION POLICIES nav, not PlanDetailView)
 *   readOnly         — bool  — true when viewing archived context
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants — DDL-authoritative
// ---------------------------------------------------------------------------

const PARTNER_TYPES = [
  { value: 'reseller',           label: 'Reseller' },
  { value: 'distributor',        label: 'Distributor' },
  { value: 'technology_partner', label: 'Technology partner' },
  { value: 'referral',           label: 'Referral partner' },
];

const TABS = [
  { key: 'partners',   label: 'Partner Entities'  },
  { key: 'agreements', label: 'Co-Sell Agreements' },
  { key: 'results',    label: 'Run Results'        },
];

const EMPTY_PARTNER_FORM = {
  partner_code:  '',
  partner_name:  '',
  partner_type:  'reseller',
  contact_email: '',
};

const EMPTY_AGREEMENT_FORM = {
  partner_entity_id:         '',
  position_id:               '',
  credit_id:                 '',
  commission_method:         'pct',  // 'pct' | 'amount' — UI-only field driving XOR
  partner_commission_pct:    '',
  partner_commission_amount: '',
  agreement_date:            '',
  notes:                     '',
};

// ---------------------------------------------------------------------------
// Shared CSS class strings — token-compliant
// ---------------------------------------------------------------------------

const thClass =
  'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide ' +
  'text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] ' +
  'border-b border-[rgb(var(--color-border))]';

const tdClass =
  'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] ' +
  'border-b border-[rgb(var(--color-border))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnSecondary =
  'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] ' +
  'px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] ' +
  'hover:bg-[rgb(var(--color-surface-muted))] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg border border-[rgb(var(--color-danger))]/40 px-3 py-1.5 text-tok-sm ' +
  'font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const inputClass =
  'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] ' +
  'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] ' +
  'px-3 py-2 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const labelClass = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (!entity) return 'draft';
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.detail || fallback;
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function fmtDate(val) {
  if (!val) return '—';
  return String(val).slice(0, 10);
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return '—';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(val) {
  if (val === null || val === undefined || val === '') return '—';
  return `${(Number(val) * 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CoSellManager({ mode, period, wizardSteps, embedded = false, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.cosell')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [activeTab, setActiveTab]                   = useState('partners');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                           = useState(null);

  // Partners
  const [partners, setPartners]                     = useState([]);
  const [partnersLoading, setPartnersLoading]       = useState(false);
  const [partnersError, setPartnersError]           = useState('');
  const [filterActive, setFilterActive]             = useState('true');
  const [selectedPartner, setSelectedPartner]       = useState(null);
  const [partnerDetailLoading, setPartnerDetailLoading] = useState(false);
  const [showPartnerForm, setShowPartnerForm]       = useState(false);
  const [partnerForm, setPartnerForm]               = useState(EMPTY_PARTNER_FORM);
  const [partnerSaving, setPartnerSaving]           = useState(false);
  const [partnerFormError, setPartnerFormError]     = useState('');
  const [editingPartnerId, setEditingPartnerId]     = useState(null);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);
  const [deactivateLoading, setDeactivateLoading]   = useState(false);
  const [deactivateError, setDeactivateError]       = useState('');

  // Agreements
  const [agreements, setAgreements]                 = useState([]);
  const [agreementsLoading, setAgreementsLoading]   = useState(false);
  const [agreementsError, setAgreementsError]       = useState('');
  const [agreementFilters, setAgreementFilters]     = useState({ partner: '', position: '', credit: '' });
  const [selectedAgreement, setSelectedAgreement]   = useState(null);
  const [agreementDetailLoading, setAgreementDetailLoading] = useState(false);
  const [actionLoading, setActionLoading]           = useState(false);
  const [actionError, setActionError]               = useState('');
  const [showAgreementForm, setShowAgreementForm]   = useState(false);
  const [agreementForm, setAgreementForm]           = useState(EMPTY_AGREEMENT_FORM);
  const [agreementSaving, setAgreementSaving]       = useState(false);
  const [agreementFormError, setAgreementFormError] = useState('');
  const [editingAgreementId, setEditingAgreementId] = useState(null);

  // Positions reference
  const [positions, setPositions]                   = useState([]);

  // Results tab
  const [runIdInput, setRunIdInput]                 = useState('');
  const [partnerIdInput, setPartnerIdInput]         = useState('');
  const [results, setResults]                       = useState([]);
  const [resultsLoading, setResultsLoading]         = useState(false);
  const [resultsError, setResultsError]             = useState('');

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const currentUserId = user?.user_id || user?.id || null;

  // ---------------------------------------------------------------------------
  // Load partners
  // ---------------------------------------------------------------------------

  const loadPartners = useCallback(async () => {
    if (!user) return;
    setPartnersLoading(true);
    setPartnersError('');
    try {
      const params = new URLSearchParams();
      if (filterActive !== '') params.set('is_active', filterActive);
      const url = `/api/v1/partner-entities${params.toString() ? `?${params}` : ''}`;
      const data = await apiGet(url);
      setPartners(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'FEATURE_UNAVAILABLE' || error?.status === 403) {
        setFeatureUnavailable(true);
        return;
      }
      setPartnersError(getErrorMessage(error, 'Unable to load partner entities.'));
    } finally {
      setPartnersLoading(false);
    }
  }, [user, filterActive]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.positions || data?.data || []);
    } catch { /* non-blocking */ }
  }, [user]);

  useEffect(() => { loadPartners(); loadPositions(); }, [loadPartners, loadPositions]);

  // ---------------------------------------------------------------------------
  // Load partner detail
  // ---------------------------------------------------------------------------

  const loadPartnerDetail = useCallback(async (partnerId) => {
    setPartnerDetailLoading(true);
    try {
      const data = await apiGet(`/api/v1/partner-entities/${partnerId}`);
      setSelectedPartner(data);
    } catch (error) {
      setPartnersError(getErrorMessage(error, 'Unable to load partner detail.'));
    } finally {
      setPartnerDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Load agreements
  // ---------------------------------------------------------------------------

  const loadAgreements = useCallback(async () => {
    setAgreementsLoading(true);
    setAgreementsError('');
    try {
      const params = new URLSearchParams();
      if (agreementFilters.partner)  params.set('partner_entity_id', agreementFilters.partner);
      if (agreementFilters.position) params.set('position_id', agreementFilters.position);
      if (agreementFilters.credit)   params.set('credit_id', agreementFilters.credit);
      const url = `/api/v1/cosell-agreements${params.toString() ? `?${params}` : ''}`;
      const data = await apiGet(url);
      setAgreements(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setAgreementsError(getErrorMessage(error, 'Unable to load co-sell agreements.'));
    } finally {
      setAgreementsLoading(false);
    }
  }, [agreementFilters]);

  useEffect(() => {
    if (activeTab === 'agreements') loadAgreements();
  }, [activeTab, loadAgreements]);

  // ---------------------------------------------------------------------------
  // Load agreement detail
  // ---------------------------------------------------------------------------

  const loadAgreementDetail = useCallback(async (agreementId) => {
    setAgreementDetailLoading(true);
    setActionError('');
    try {
      const data = await apiGet(`/api/v1/cosell-agreements/${agreementId}`);
      setSelectedAgreement(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load agreement detail.'));
    } finally {
      setAgreementDetailLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Partner form helpers
  // ---------------------------------------------------------------------------

  const updatePartnerForm = useCallback((field, value) =>
    setPartnerForm((prev) => ({ ...prev, [field]: value })), []);

  const resetPartnerForm = useCallback(() => {
    setPartnerForm(EMPTY_PARTNER_FORM);
    setPartnerFormError('');
    setEditingPartnerId(null);
  }, []);

  const validatePartnerForm = useCallback(() => {
    if (!partnerForm.partner_code.trim()) return 'Partner code is required.';
    if (!partnerForm.partner_name.trim()) return 'Partner name is required.';
    if (!partnerForm.partner_type)        return 'Partner type is required.';
    if (partnerForm.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partnerForm.contact_email))
      return 'Contact email format is invalid.';
    return '';
  }, [partnerForm]);

  // ---------------------------------------------------------------------------
  // Create partner
  // ---------------------------------------------------------------------------

  const createPartner = useCallback(async () => {
    const err = validatePartnerForm();
    if (err) { setPartnerFormError(err); return; }
    setPartnerSaving(true);
    setPartnerFormError('');
    try {
      const payload = {
        partner_code:  partnerForm.partner_code.trim(),
        partner_name:  partnerForm.partner_name.trim(),
        partner_type:  partnerForm.partner_type,
        contact_email: partnerForm.contact_email.trim() || null,
      };
      const created = await apiPost('/api/v1/partner-entities', payload);
      await loadPartners();
      setShowPartnerForm(false);
      resetPartnerForm();
      showToast('Partner entity created.');
      const newId = created?.partner_entity_id;
      if (newId) await loadPartnerDetail(newId);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'PARTNER_CODE_EXISTS')
        setPartnerFormError(`Partner code "${partnerForm.partner_code}" already exists. Choose a unique code.`);
      else
        setPartnerFormError(getErrorMessage(error, 'Unable to create partner entity.'));
    } finally {
      setPartnerSaving(false);
    }
  }, [loadPartnerDetail, loadPartners, partnerForm, resetPartnerForm, showToast, validatePartnerForm]);

  // ---------------------------------------------------------------------------
  // Update partner (PUT — raw fetch)
  // ---------------------------------------------------------------------------

  const updatePartner = useCallback(async () => {
    if (!editingPartnerId) return;
    const err = validatePartnerForm();
    if (err) { setPartnerFormError(err); return; }
    setPartnerSaving(true);
    setPartnerFormError('');
    try {
      const res = await fetch(`/api/v1/partner-entities/${editingPartnerId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partner_name:  partnerForm.partner_name.trim(),
          partner_type:  partnerForm.partner_type,
          contact_email: partnerForm.contact_email.trim() || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadPartnerDetail(editingPartnerId);
      await loadPartners();
      setShowPartnerForm(false);
      resetPartnerForm();
      showToast('Partner entity updated.');
    } catch (error) {
      setPartnerFormError(error.message || 'Unable to update partner entity.');
    } finally {
      setPartnerSaving(false);
    }
  }, [editingPartnerId, loadPartnerDetail, loadPartners, partnerForm, resetPartnerForm, showToast, validatePartnerForm]);

  const openEditPartner = useCallback((partner) => {
    setEditingPartnerId(partner.partner_entity_id);
    setPartnerForm({
      partner_code:  partner.partner_code || '',
      partner_name:  partner.partner_name || '',
      partner_type:  partner.partner_type || 'reseller',
      contact_email: partner.contact_email || '',
    });
    setPartnerFormError('');
    setShowPartnerForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Deactivate partner (DELETE — soft, raw fetch)
  // ---------------------------------------------------------------------------

  const deactivatePartner = useCallback(async (partnerId) => {
    setDeactivateLoading(true);
    setDeactivateError('');
    try {
      const res = await fetch(`/api/v1/partner-entities/${partnerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'PARTNER_ENTITY_HAS_ACTIVE_AGREEMENTS')
          throw new Error('This partner has active approved co-sell agreements and cannot be deactivated.');
        throw new Error(body?.error?.message || body?.detail || `Deactivate failed (${res.status})`);
      }
      setConfirmDeactivateId(null);
      if (selectedPartner?.partner_entity_id === partnerId) {
        setSelectedPartner(null);
      }
      await loadPartners();
      showToast('Partner entity deactivated.');
    } catch (error) {
      setDeactivateError(error.message || 'Unable to deactivate partner entity.');
    } finally {
      setDeactivateLoading(false);
    }
  }, [loadPartners, selectedPartner, showToast]);

  // ---------------------------------------------------------------------------
  // Agreement form helpers
  // ---------------------------------------------------------------------------

  const updateAgreementForm = useCallback((field, value) =>
    setAgreementForm((prev) => ({ ...prev, [field]: value })), []);

  const resetAgreementForm = useCallback(() => {
    setAgreementForm(EMPTY_AGREEMENT_FORM);
    setAgreementFormError('');
    setEditingAgreementId(null);
  }, []);

  const validateAgreementForm = useCallback(() => {
    if (!agreementForm.partner_entity_id)  return 'Partner entity is required.';
    if (!agreementForm.position_id)        return 'Position (internal rep) is required.';
    if (!agreementForm.credit_id.trim())   return 'Credit ID is required.';
    if (!agreementForm.agreement_date)     return 'Agreement date is required.';
    if (agreementForm.commission_method === 'pct') {
      if (!agreementForm.partner_commission_pct || isNaN(Number(agreementForm.partner_commission_pct)))
        return 'Commission percentage is required and must be a number.';
      const pct = Number(agreementForm.partner_commission_pct);
      if (pct <= 0 || pct > 1) return 'Commission % must be between 0.0001 and 1.0000.';
    } else {
      if (!agreementForm.partner_commission_amount || isNaN(Number(agreementForm.partner_commission_amount)))
        return 'Commission amount is required and must be a number.';
      if (Number(agreementForm.partner_commission_amount) <= 0) return 'Commission amount must be positive.';
    }
    return '';
  }, [agreementForm]);

  // Build XOR-safe payload from form
  const buildAgreementPayload = useCallback(() => ({
    partner_entity_id:         agreementForm.partner_entity_id,
    position_id:               agreementForm.position_id,
    credit_id:                 agreementForm.credit_id.trim(),
    partner_commission_pct:    agreementForm.commission_method === 'pct'
                                 ? agreementForm.partner_commission_pct : null,
    partner_commission_amount: agreementForm.commission_method === 'amount'
                                 ? agreementForm.partner_commission_amount : null,
    agreement_date:            agreementForm.agreement_date,
    notes:                     agreementForm.notes.trim() || null,
  }), [agreementForm]);

  // ---------------------------------------------------------------------------
  // Create agreement
  // ---------------------------------------------------------------------------

  const createAgreement = useCallback(async () => {
    const err = validateAgreementForm();
    if (err) { setAgreementFormError(err); return; }
    setAgreementSaving(true);
    setAgreementFormError('');
    try {
      const created = await apiPost('/api/v1/cosell-agreements', buildAgreementPayload());
      await loadAgreements();
      setShowAgreementForm(false);
      resetAgreementForm();
      showToast('Co-sell agreement created.');
      const newId = created?.cosell_agreement_id;
      if (newId) await loadAgreementDetail(newId);
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'COSELL_COMMISSION_BOTH_SET')
        setAgreementFormError('Only one commission method (% or fixed amount) is allowed.');
      else if (code === 'COSELL_COMMISSION_NEITHER_SET')
        setAgreementFormError('At least one commission method is required.');
      else if (code === 'COSELL_AGREEMENT_DATE_INVALID')
        setAgreementFormError('Agreement date must be on or before the deal close date.');
      else if (code === 'PARTNER_ENTITY_NOT_FOUND')
        setAgreementFormError('Selected partner entity not found. It may have been deactivated.');
      else
        setAgreementFormError(getErrorMessage(error, 'Unable to create co-sell agreement.'));
    } finally {
      setAgreementSaving(false);
    }
  }, [buildAgreementPayload, loadAgreementDetail, loadAgreements, resetAgreementForm, showToast, validateAgreementForm]);

  // ---------------------------------------------------------------------------
  // Update agreement (PUT — raw fetch, not approved only)
  // ---------------------------------------------------------------------------

  const updateAgreement = useCallback(async () => {
    if (!editingAgreementId) return;
    const err = validateAgreementForm();
    if (err) { setAgreementFormError(err); return; }
    setAgreementSaving(true);
    setAgreementFormError('');
    try {
      const res = await fetch(`/api/v1/cosell-agreements/${editingAgreementId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildAgreementPayload()),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error?.code || body?.code;
        if (code === 'CONFIG_ALREADY_APPROVED')
          throw new Error('This agreement is already approved and cannot be edited.');
        if (code === 'COSELL_AGREEMENT_DATE_INVALID')
          throw new Error('Agreement date must be on or before the deal close date.');
        if (code === 'COSELL_COMMISSION_BOTH_SET')
          throw new Error('Only one commission method is allowed.');
        throw new Error(body?.error?.message || body?.detail || `Update failed (${res.status})`);
      }
      await loadAgreementDetail(editingAgreementId);
      await loadAgreements();
      setShowAgreementForm(false);
      resetAgreementForm();
      showToast('Agreement updated.');
    } catch (error) {
      setAgreementFormError(error.message || 'Unable to update agreement.');
    } finally {
      setAgreementSaving(false);
    }
  }, [buildAgreementPayload, editingAgreementId, loadAgreementDetail, loadAgreements, resetAgreementForm, showToast, validateAgreementForm]);

  const openEditAgreement = useCallback((agreement) => {
    setEditingAgreementId(agreement.cosell_agreement_id);
    const hasPct = agreement.partner_commission_pct != null;
    setAgreementForm({
      partner_entity_id:         agreement.partner_entity_id || '',
      position_id:               agreement.position_id || '',
      credit_id:                 agreement.credit_id || '',
      commission_method:         hasPct ? 'pct' : 'amount',
      partner_commission_pct:    hasPct ? String(agreement.partner_commission_pct) : '',
      partner_commission_amount: !hasPct && agreement.partner_commission_amount != null
                                   ? String(agreement.partner_commission_amount) : '',
      agreement_date:            fmtDate(agreement.agreement_date) === '—' ? '' : fmtDate(agreement.agreement_date),
      notes:                     agreement.notes || '',
    });
    setAgreementFormError('');
    setShowAgreementForm(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Agreement approval actions
  // ---------------------------------------------------------------------------

  const submitAgreement = useCallback(async () => {
    if (!selectedAgreement) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/cosell-agreements/${selectedAgreement.cosell_agreement_id}/submit`, {});
      await loadAgreementDetail(selectedAgreement.cosell_agreement_id);
      await loadAgreements();
      showToast('Agreement submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit agreement.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadAgreementDetail, loadAgreements, selectedAgreement, showToast]);

  const approveAgreement = useCallback(async () => {
    if (!selectedAgreement) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/cosell-agreements/${selectedAgreement.cosell_agreement_id}/approve`, {});
      await loadAgreementDetail(selectedAgreement.cosell_agreement_id);
      await loadAgreements();
      showToast('Agreement approved.');
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_BLOCKED')
        setActionError('You submitted this agreement — a different user must approve it.');
      else
        setActionError(getErrorMessage(error, 'Unable to approve agreement.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadAgreementDetail, loadAgreements, selectedAgreement, showToast]);

  // ---------------------------------------------------------------------------
  // Results tab
  // ---------------------------------------------------------------------------

  const loadResults = useCallback(async () => {
    if (!runIdInput.trim()) { setResultsError('Calculation run ID is required.'); return; }
    setResultsLoading(true);
    setResultsError('');
    setResults([]);
    try {
      const params = new URLSearchParams({ calculation_run_id: runIdInput.trim() });
      if (partnerIdInput.trim()) params.set('partner_entity_id', partnerIdInput.trim());
      const data = await apiGet(`/api/v1/cosell-payout-results?${params}`);
      setResults(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setResultsError(getErrorMessage(error, 'Unable to load co-sell results.'));
    } finally {
      setResultsLoading(false);
    }
  }, [partnerIdInput, runIdInput]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const activePartners = useMemo(() => partners.filter((p) => p.is_active), [partners]);

  const partnerNameById = useMemo(
    () => partners.reduce((acc, p) => {
      acc[String(p.partner_entity_id)] = p.partner_name;
      return acc;
    }, {}),
    [partners]
  );

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => {
      acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`;
      return acc;
    }, {}),
    [positions]
  );

  // ---------------------------------------------------------------------------
  // Feature gate / auth guard
  // ---------------------------------------------------------------------------

  if (featureUnavailable) {
    return (
      <GateStatusBanner
        feature="Channel / Co-Sell Compensation"
        message="Co-sell compensation requires the Enterprise tier (feature.tier.cosell). Contact your account manager to enable this feature."
      />
    );
  }

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Co-Sell Manager</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Partner detail view
  // ---------------------------------------------------------------------------

  if (selectedPartner) {
    const pt = PARTNER_TYPES.find((t) => t.value === selectedPartner.partner_type);
    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => { setSelectedPartner(null); setShowPartnerForm(false); resetPartnerForm(); setDeactivateError(''); }}>
              ← Back to partners
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedPartner.partner_name}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {selectedPartner.partner_code} · {pt?.label || selectedPartner.partner_type}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
              selectedPartner.is_active
                ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
            }`}>
              {selectedPartner.is_active ? 'Active' : 'Inactive'}
            </span>
            {selectedPartner.is_active && (
              <button type="button" className={btnSecondary} onClick={() => openEditPartner(selectedPartner)}>
                Edit
              </button>
            )}
          </div>
        </header>

        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Partner details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Partner code</dt>
              <dd className="text-[rgb(var(--color-text))] font-mono text-tok-xs">{selectedPartner.partner_code}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Type</dt>
              <dd className="text-[rgb(var(--color-text))]">{pt?.label || selectedPartner.partner_type}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Contact email</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPartner.contact_email || '—'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Created</dt>
              <dd className="text-[rgb(var(--color-text))]">{fmtDate(selectedPartner.created_at)}</dd>
            </div>
          </dl>
        </div>

        {/* Inline edit form */}
        {showPartnerForm && editingPartnerId === selectedPartner.partner_entity_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit partner</h3>
            {partnerFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {partnerFormError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={labelClass + ' sm:col-span-2'}>
                Partner name <span className="text-[rgb(var(--color-danger))]">*</span>
                <input type="text" className={inputClass} value={partnerForm.partner_name}
                  onChange={(e) => updatePartnerForm('partner_name', e.target.value)} />
              </label>
              <label className={labelClass}>
                Partner type <span className="text-[rgb(var(--color-danger))]">*</span>
                <select className={inputClass} value={partnerForm.partner_type}
                  onChange={(e) => updatePartnerForm('partner_type', e.target.value)}>
                  {PARTNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className={labelClass}>
                Contact email
                <input type="email" className={inputClass} value={partnerForm.contact_email}
                  onChange={(e) => updatePartnerForm('contact_email', e.target.value)} />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updatePartner} disabled={partnerSaving}>
                {partnerSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowPartnerForm(false); resetPartnerForm(); }} disabled={partnerSaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Deactivate */}
        {selectedPartner.is_active && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Deactivate partner</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Deactivating sets this partner to inactive. New co-sell agreements cannot be created for inactive partners.
              Cannot deactivate if active approved agreements reference this partner.
            </p>
            {deactivateError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {deactivateError}
              </div>
            )}
            {confirmDeactivateId === selectedPartner.partner_entity_id ? (
              <div className="flex items-center gap-2 text-tok-sm flex-wrap">
                <span className="text-[rgb(var(--color-text-muted))]">Deactivate {selectedPartner.partner_name}?</span>
                <button type="button" className={btnDanger}
                  onClick={() => deactivatePartner(selectedPartner.partner_entity_id)}
                  disabled={deactivateLoading}>
                  {deactivateLoading ? 'Deactivating…' : 'Confirm deactivate'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setConfirmDeactivateId(null); setDeactivateError(''); }}
                  disabled={deactivateLoading}>
                  Cancel
                </button>
              </div>
            ) : (
              <div>
                <button type="button" className={btnDanger}
                  onClick={() => setConfirmDeactivateId(selectedPartner.partner_entity_id)}>
                  Deactivate partner
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Agreement detail view
  // ---------------------------------------------------------------------------

  if (selectedAgreement) {
    const state     = deriveState(selectedAgreement);
    const isDraft   = state === 'draft';
    const hasPct    = selectedAgreement.partner_commission_pct != null;
    const partName  = partnerNameById[String(selectedAgreement.partner_entity_id)] || selectedAgreement.partner_entity_id;
    const posName   = positionNameById[String(selectedAgreement.position_id)] || selectedAgreement.position_id;

    return (
      <section className="flex flex-col gap-6">
        {toast && (
          <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
            {toast}
          </div>
        )}

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary}
              onClick={() => { setSelectedAgreement(null); setActionError(''); setShowAgreementForm(false); resetAgreementForm(); }}>
              ← Back to agreements
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              Co-Sell Agreement
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {partName} · {posName} · Agreement date {fmtDate(selectedAgreement.agreement_date)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={state} />
            {isDraft && (
              <button type="button" className={btnSecondary} onClick={() => openEditAgreement(selectedAgreement)}>
                Edit
              </button>
            )}
          </div>
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Agreement details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Partner</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium">{partName}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Internal rep</dt>
              <dd className="text-[rgb(var(--color-text))]">{posName}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Commission</dt>
              <dd className="text-[rgb(var(--color-text))] font-medium">
                {hasPct
                  ? `${fmtPct(selectedAgreement.partner_commission_pct)} of deal value`
                  : `${fmtMoney(selectedAgreement.partner_commission_amount)} fixed`}
              </dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Agreement date</dt>
              <dd className="text-[rgb(var(--color-text))]">{fmtDate(selectedAgreement.agreement_date)}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Credit ID</dt>
              <dd className="text-tok-xs text-[rgb(var(--color-text-muted))] break-all">{selectedAgreement.credit_id}</dd>
            </div>
            {selectedAgreement.notes && (
              <div className="sm:col-span-2">
                <dt className="text-[rgb(var(--color-text-muted))] font-medium">Notes</dt>
                <dd className="text-[rgb(var(--color-text))]">{selectedAgreement.notes}</dd>
              </div>
            )}
          </dl>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
            Note: co-sell commission is paid to the partner separately. It does not reduce the internal rep's payout.
          </p>
        </div>

        {/* Inline edit form */}
        {showAgreementForm && editingAgreementId === selectedAgreement.cosell_agreement_id && (
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Edit agreement</h3>
            {agreementFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {agreementFormError}
              </div>
            )}
            {renderAgreementFormFields({ agreementForm, updateAgreementForm, activePartners, positions, positionNameById, inputClass, labelClass })}
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={updateAgreement} disabled={agreementSaving}>
                {agreementSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className={btnSecondary}
                onClick={() => { setShowAgreementForm(false); resetAgreementForm(); }} disabled={agreementSaving}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Approval panel */}
        {agreementDetailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="plan"
            currentState={state}
            submittedBy={selectedAgreement.submitted_by ?? null}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? submitAgreement : null}
            onApprove={state === 'pending_approval' ? approveAgreement : null}
            onReject={null}
            approvedBy={selectedAgreement.approved_by ?? null}
            approvedAt={selectedAgreement.approved_at ?? null}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <header>
        {mode === 'wizard' && (
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            Stage 8 · P3-06 · Enterprise
          </p>
        )}
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Channel / Co-Sell Compensation</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Register external partner entities and create co-sell agreements for deals they influenced.
          Partner commission is calculated separately and does not affect the internal rep's payout.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Partners tab ── */}
      {activeTab === 'partners' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-3 flex-wrap">
            <label className={labelClass} style={{ minWidth: '160px' }}>
              Show
              <select className={inputClass} value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
                <option value="">All</option>
              </select>
            </label>
            <button type="button" className={btnSecondary} onClick={loadPartners} disabled={partnersLoading}>
              {partnersLoading ? 'Loading…' : 'Refresh'}
            </button>
            {!showPartnerForm && (
              <button type="button" className={btnPrimary}
                onClick={() => { resetPartnerForm(); setShowPartnerForm(true); }}>
                + New partner
              </button>
            )}
          </div>

          {partnersError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {partnersError}
            </div>
          )}

          {/* Partner create form */}
          {showPartnerForm && !editingPartnerId && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New partner entity</h3>
              {partnerFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {partnerFormError}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={labelClass}>
                  Partner code <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={partnerForm.partner_code}
                    onChange={(e) => updatePartnerForm('partner_code', e.target.value)}
                    placeholder="e.g. ACME-RESELL-001 — unique, immutable after creation" />
                </label>
                <label className={labelClass}>
                  Partner name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" className={inputClass} value={partnerForm.partner_name}
                    onChange={(e) => updatePartnerForm('partner_name', e.target.value)}
                    placeholder="e.g. Acme Technology Partners LLC" />
                </label>
                <label className={labelClass}>
                  Partner type <span className="text-[rgb(var(--color-danger))]">*</span>
                  <select className={inputClass} value={partnerForm.partner_type}
                    onChange={(e) => updatePartnerForm('partner_type', e.target.value)}>
                    {PARTNER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className={labelClass}>
                  Contact email
                  <input type="email" className={inputClass} value={partnerForm.contact_email}
                    onChange={(e) => updatePartnerForm('contact_email', e.target.value)}
                    placeholder="e.g. commissions@acme.com" />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createPartner} disabled={partnerSaving}>
                  {partnerSaving ? 'Creating…' : 'Create partner'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowPartnerForm(false); resetPartnerForm(); }} disabled={partnerSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Partners list */}
          {!partnersLoading && partners.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No partner entities found. Register partner companies and individuals who co-sell or refer deals.
              </p>
            </div>
          )}
          {partners.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Partner name</th>
                    <th className={thClass}>Code</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Contact</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr key={p.partner_entity_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium'}>{p.partner_name}</td>
                      <td className={tdClass + ' font-mono text-tok-xs'}>{p.partner_code}</td>
                      <td className={tdClass}>{PARTNER_TYPES.find((t) => t.value === p.partner_type)?.label || p.partner_type}</td>
                      <td className={tdClass}>{p.contact_email || '—'}</td>
                      <td className={tdClass}>
                        <span className={`inline-block px-2 py-0.5 rounded text-tok-xs font-medium ${
                          p.is_active
                            ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                            : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                        }`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className={tdClass}>
                        <button type="button"
                          className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                          onClick={() => loadPartnerDetail(p.partner_entity_id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Agreements tab ── */}
      {activeTab === 'agreements' && (
        <div className="flex flex-col gap-4">
          {/* Filters */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 flex items-end gap-3 flex-wrap shadow-sm">
            <label className={labelClass} style={{ flex: '1', minWidth: '160px' }}>
              Partner
              <select className={inputClass} value={agreementFilters.partner}
                onChange={(e) => setAgreementFilters((prev) => ({ ...prev, partner: e.target.value }))}>
                <option value="">All partners</option>
                {partners.map((p) => (
                  <option key={p.partner_entity_id} value={p.partner_entity_id}>{p.partner_name}</option>
                ))}
              </select>
            </label>
            <label className={labelClass} style={{ flex: '1', minWidth: '180px' }}>
              Credit ID (optional)
              <input type="text" className={inputClass} value={agreementFilters.credit}
                onChange={(e) => setAgreementFilters((prev) => ({ ...prev, credit: e.target.value }))}
                placeholder="Filter by deal credit UUID" />
            </label>
            <button type="button" className={btnSecondary} onClick={loadAgreements} disabled={agreementsLoading}>
              {agreementsLoading ? 'Loading…' : 'Search'}
            </button>
            {!showAgreementForm && (
              <button type="button" className={btnPrimary}
                onClick={() => { resetAgreementForm(); setShowAgreementForm(true); }}>
                + New agreement
              </button>
            )}
          </div>

          {agreementsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {agreementsError}
            </div>
          )}

          {/* Agreement create form */}
          {showAgreementForm && !editingAgreementId && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New co-sell agreement</h3>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Agreement date must be on or before the deal close date. Provide either a percentage of deal value
                or a pre-agreed fixed commission amount — not both.
              </p>
              {agreementFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {agreementFormError}
                </div>
              )}
              {renderAgreementFormFields({ agreementForm, updateAgreementForm, activePartners, positions, positionNameById, inputClass, labelClass })}
              <div className="flex items-center gap-2">
                <button type="button" className={btnPrimary} onClick={createAgreement} disabled={agreementSaving}>
                  {agreementSaving ? 'Creating…' : 'Create agreement'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowAgreementForm(false); resetAgreementForm(); }} disabled={agreementSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Agreements list */}
          {!agreementsLoading && agreements.length === 0 && (
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-5 py-8 text-center">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                No co-sell agreements found. Create agreements for deals where an external partner was involved.
                Agreements must be approved before the engine can compute partner commission.
              </p>
            </div>
          )}
          {agreements.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Partner</th>
                    <th className={thClass}>Rep</th>
                    <th className={thClass}>Commission</th>
                    <th className={thClass}>Agreement date</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((a) => (
                    <tr key={a.cosell_agreement_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium'}>{partnerNameById[String(a.partner_entity_id)] || a.partner_entity_id}</td>
                      <td className={tdClass}>{positionNameById[String(a.position_id)] || a.position_id}</td>
                      <td className={tdClass}>
                        {a.partner_commission_pct != null
                          ? fmtPct(a.partner_commission_pct)
                          : fmtMoney(a.partner_commission_amount)}
                      </td>
                      <td className={tdClass}>{fmtDate(a.agreement_date)}</td>
                      <td className={tdClass}><StatusPill status={deriveState(a)} /></td>
                      <td className={tdClass}>
                        <button type="button"
                          className="text-tok-sm text-[rgb(var(--color-primary))] hover:underline"
                          onClick={() => loadAgreementDetail(a.cosell_agreement_id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Results tab ── */}
      {activeTab === 'results' && (
        <div className="flex flex-col gap-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            View partner commission amounts computed for a calculation run.
            Results are separate from internal rep payouts — each row represents
            a financial obligation to the partner.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className={labelClass}>
              Calculation run ID <span className="text-[rgb(var(--color-danger))]">*</span>
              <input type="text" className={inputClass} value={runIdInput}
                onChange={(e) => setRunIdInput(e.target.value)}
                placeholder="UUID from calculation run" />
            </label>
            <label className={labelClass}>
              Partner entity ID (optional)
              <select className={inputClass} value={partnerIdInput}
                onChange={(e) => setPartnerIdInput(e.target.value)}>
                <option value="">All partners</option>
                {partners.map((p) => (
                  <option key={p.partner_entity_id} value={p.partner_entity_id}>{p.partner_name}</option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <button type="button" className={btnPrimary} onClick={loadResults} disabled={resultsLoading}>
              {resultsLoading ? 'Loading…' : 'Load results'}
            </button>
          </div>
          {resultsError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {resultsError}
            </div>
          )}
          {!resultsLoading && results.length === 0 && runIdInput && !resultsError && (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
              No co-sell results found for this run. No approved agreements may have matched
              credits in this period, or the run ID is incorrect.
            </p>
          )}
          {results.length > 0 && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-sm overflow-x-auto">
              <table className="min-w-full text-tok-sm border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Partner</th>
                    <th className={thClass}>Internal rep</th>
                    <th className={thClass}>Deal value</th>
                    <th className={thClass}>Commission %</th>
                    <th className={thClass}>Partner commission</th>
                    <th className={thClass}>Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.cosell_result_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className={tdClass + ' font-medium'}>
                        {partnerNameById[String(r.partner_entity_id)] || r.partner_entity_id}
                      </td>
                      <td className={tdClass}>{positionNameById[String(r.position_id)] || r.position_id}</td>
                      <td className={tdClass}>{fmtMoney(r.deal_value)}</td>
                      <td className={tdClass}>{r.partner_commission_pct != null ? fmtPct(r.partner_commission_pct) : '—'}</td>
                      <td className={tdClass + ' font-medium'}>{fmtMoney(r.partner_commission_amount)}</td>
                      <td className={tdClass}>{r.payout_currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Extracted agreement form fields — used in create and edit contexts
// ---------------------------------------------------------------------------

function renderAgreementFormFields({ agreementForm, updateAgreementForm, activePartners, positions, positionNameById, inputClass, labelClass }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className={labelClass}>
        Partner entity <span className="text-[rgb(var(--color-danger))]">*</span>
        <select className={inputClass} value={agreementForm.partner_entity_id}
          onChange={(e) => updateAgreementForm('partner_entity_id', e.target.value)}>
          <option value="">Select a partner…</option>
          {activePartners.map((p) => (
            <option key={p.partner_entity_id} value={p.partner_entity_id}>{p.partner_name}</option>
          ))}
        </select>
      </label>
      <label className={labelClass}>
        Internal rep (position) <span className="text-[rgb(var(--color-danger))]">*</span>
        <select className={inputClass} value={agreementForm.position_id}
          onChange={(e) => updateAgreementForm('position_id', e.target.value)}>
          <option value="">Select a position…</option>
          {positions.map((p) => (
            <option key={p.position_id} value={p.position_id}>
              {p.full_name || `Position ${p.position_id}`}
            </option>
          ))}
        </select>
      </label>
      <label className={labelClass}>
        Credit ID <span className="text-[rgb(var(--color-danger))]">*</span>
        <input type="text" className={inputClass} value={agreementForm.credit_id}
          onChange={(e) => updateAgreementForm('credit_id', e.target.value)}
          placeholder="UUID of the deal credit being co-sold" />
      </label>
      <label className={labelClass}>
        Agreement date <span className="text-[rgb(var(--color-danger))]">*</span>
        <input type="date" className={inputClass} value={agreementForm.agreement_date}
          onChange={(e) => updateAgreementForm('agreement_date', e.target.value)} />
        <span className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Must be on or before the deal close date
        </span>
      </label>

      {/* Commission method XOR */}
      <div className="sm:col-span-2">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] mb-2">
          Commission method <span className="text-[rgb(var(--color-danger))]">*</span>
          <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
            Choose exactly one
          </span>
        </p>
        <div className="flex gap-4 mb-3">
          <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer">
            <input type="radio" name="commission_method"
              checked={agreementForm.commission_method === 'pct'}
              onChange={() => updateAgreementForm('commission_method', 'pct')} />
            Percentage of deal value
          </label>
          <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))] cursor-pointer">
            <input type="radio" name="commission_method"
              checked={agreementForm.commission_method === 'amount'}
              onChange={() => updateAgreementForm('commission_method', 'amount')} />
            Fixed amount
          </label>
        </div>
        {agreementForm.commission_method === 'pct' ? (
          <label className={labelClass} style={{ maxWidth: '280px' }}>
            Partner commission % <span className="text-[rgb(var(--color-danger))]">*</span>
            <input type="number" min="0.0001" max="1" step="0.0001" className={inputClass}
              value={agreementForm.partner_commission_pct}
              onChange={(e) => updateAgreementForm('partner_commission_pct', e.target.value)}
              placeholder="e.g. 0.1000 for 10%" />
          </label>
        ) : (
          <label className={labelClass} style={{ maxWidth: '280px' }}>
            Fixed commission amount <span className="text-[rgb(var(--color-danger))]">*</span>
            <input type="number" min="0.01" step="0.01" className={inputClass}
              value={agreementForm.partner_commission_amount}
              onChange={(e) => updateAgreementForm('partner_commission_amount', e.target.value)}
              placeholder="e.g. 30000.00" />
          </label>
        )}
      </div>

      <label className={labelClass + ' sm:col-span-2'}>
        Notes
        <textarea className={inputClass + ' min-h-16 resize-y'} rows={2}
          value={agreementForm.notes}
          onChange={(e) => updateAgreementForm('notes', e.target.value)}
          placeholder="Optional — e.g. partner influenced intro per Q1 program" />
      </label>
    </div>
  );
}
